import { useState } from 'react';
import {
  Button,
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@@/ui/primitives';
import { PlusIcon } from 'lucide-react';
import { SchemaFieldType } from '../../schemas/entity';
import { cn } from '@@/lib/utils';
import z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PopoverClose } from '@radix-ui/react-popover';

const createSchema = z.object({
  name: z.string().min(1).max(64),
  type: z.enum(SchemaFieldType)
});

export function CreateField(props: { onSubmit: (data: { name: string; type: SchemaFieldType }) => void }) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      type: SchemaFieldType.STRING
    }
  });

  const handleSubmit = (data: z.infer<typeof createSchema>) => {
    props.onSubmit(data);
    form.reset();
    setOpen(false);
  };

  return (
    <div className={cn('group cursor-pointer', 'flex flex-row items-center justify-end h-10 w-full', 'px-1')}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant='ghost' className='text-sm! text-foreground' size='sm'>
            <PlusIcon className='size-4 mt-px' />
            Add Field
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-80 font-sans! flex flex-col gap-4' side='bottom' align='end'>
          <div className='space-y-2'>
            <h4 className='leading-none font-medium'>Add a Field</h4>
            <p className='text-muted-foreground text-xs'>
              You can modify field settings later, but you cannot delete or rename fields once data has been added.
            </p>
          </div>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='flex flex-col gap-4'>
            <Controller
              name='name'
              control={form.control}
              render={({ field, fieldState }) => (
                <Field orientation='responsive' data-invalid={fieldState.invalid} className='gap-2'>
                  <FieldContent>
                    <FieldLabel htmlFor='name'>Name</FieldLabel>
                  </FieldContent>
                  <InputGroup>
                    <InputGroupInput
                      placeholder='Name'
                      id='name'
                      value={field.value}
                      disabled={field.disabled}
                      onChange={field.onChange}
                      className='text-sm! shadow-none'
                      aria-invalid={fieldState.invalid}
                    />
                  </InputGroup>
                  <div className='text-xs text-neutral-600 px-1 max-w-xs'>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    {/* <div className='text-xs text-neutral-600'>
                    Once added, you cannot delete or rename this field unless there are no items or child
                    Entities.
                  </div> */}
                  </div>
                </Field>
              )}
            />
            <Controller
              name='type'
              control={form.control}
              render={({ field, fieldState }) => (
                <Field orientation='responsive' data-invalid={fieldState.invalid} className='gap-2'>
                  <FieldContent>
                    <FieldLabel htmlFor='form-rhf-select-type'>Type</FieldLabel>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </FieldContent>
                  <InputGroup>
                    <Select
                      name={field.name}
                      value={field.value}
                      disabled={field.disabled}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger
                        id='form-rhf-select-type'
                        aria-invalid={fieldState.invalid}
                        className='text-sm! font-medium! w-full bg-white text-neutral-950 shadow-none'
                      >
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent position='item-aligned' className='font-sans text-sm font-medium'>
                        <SelectItem value={SchemaFieldType.STRING}>String</SelectItem>
                        <SelectItem value={SchemaFieldType.NUMBER}>Number</SelectItem>
                        <SelectItem value={SchemaFieldType.BOOLEAN}>Boolean</SelectItem>
                        <SelectItem value={SchemaFieldType.DATE_TIME}>Date Time</SelectItem>
                        <SelectItem value={SchemaFieldType.REFERENCE}>Reference</SelectItem>
                        <SelectItem value={SchemaFieldType.PICK_LIST}>Pick List</SelectItem>
                        <SelectItem value={SchemaFieldType.PROMPT}>Prompt</SelectItem>
                        <SelectItem value={SchemaFieldType.OBJECT}>JSON</SelectItem>
                        <SelectItem value={SchemaFieldType.YAML}>YAML</SelectItem>
                        <SelectItem value={SchemaFieldType.CONNECT_RESOURCE}>Amazon Connect Resource</SelectItem>
                      </SelectContent>
                    </Select>
                  </InputGroup>
                </Field>
              )}
            />
            <div className='self-end flex gap-2'>
              <PopoverClose asChild>
                <Button variant='ghost' size='sm' className='text-sm! font-medium!'>
                  Cancel
                </Button>
              </PopoverClose>
              {/* <ButtonGroupSeparator /> */}
              <Button type='submit' size='sm' variant='default' className='text-sm! font-medium!'>
                <PlusIcon className='size-3.5' />
                Add
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
}
